import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import OpeningHours from "./OpeningHours";
import { 
  Mountain, 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Facebook,
  Instagram
} from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [tourSchedule, setTourSchedule] = useState({ restrict_monday_friday: true });
  const [openingHours, setOpeningHours] = useState({ closed_on_tuesdays: false });

  useEffect(() => {
    fetchTourSchedule();
    fetchOpeningHours();
  }, []);

  const fetchTourSchedule = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/tour-schedule/public`);
      if (response.ok) {
        const data = await response.json();
        setTourSchedule(data);
      }
    } catch (error) {
      console.error('Error fetching tour schedule:', error);
    }
  };

  const fetchOpeningHours = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/opening-hours/public`);
      if (response.ok) {
        const data = await response.json();
        setOpeningHours(data);
      }
    } catch (error) {
      console.error('Error fetching opening hours:', error);
    }
  };

  return (
    <footer className="bg-gray-50 border-t border-gray-200 text-gray-800 heritage-texture">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="p-1 rounded-lg">
                <img 
                  src="/assets/umoonaico.png"
                  alt="Umoona Opal Mine Logo"
                  className="w-8 h-8"
                />
              </div>
              <div>
                <h3 className="text-lg font-bold heritage-heading">Umoona Opal Mine</h3>
                <p className="text-sm text-gray-600">& Museum</p>
              </div>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed">
              The largest single underground tourist attraction in Coober Pedy. 
              Experience authentic mining history since the 1920s, free museum exhibits, 
              and genuine Australian opals.
            </p>
            <div className="flex space-x-3">
              <Button 
                asChild
                variant="ghost" 
                size="icon" 
                className="text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-colors"
              >
                <a 
                  href="https://www.facebook.com/umoona" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-label="Follow us on Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
              </Button>
              <Button 
                asChild
                variant="ghost" 
                size="icon" 
                className="text-gray-600 hover:text-purple-600 hover:bg-purple-50 transition-colors"
              >
                <a 
                  href="https://www.instagram.com/umoonaopal" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-label="Follow us on Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              </Button>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4 heritage-heading">Explore</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/about-opal" className="text-gray-600 hover:text-orange-500 transition-colors text-sm">
                  About Opal
                </Link>
              </li>
              <li>
                <Link to="/coober-pedy" className="text-gray-600 hover:text-orange-500 transition-colors text-sm">
                  Coober Pedy
                </Link>
              </li>
              <li>
                <Link to="/free-museum" className="text-gray-600 hover:text-orange-500 transition-colors text-sm">
                  Free Museum
                </Link>
              </li>
              <li>
                <Link to="/underground-tours" className="text-gray-600 hover:text-orange-500 transition-colors text-sm">
                  Underground Tours
                </Link>
              </li>
              <li>
                <Link to="/group-tours" className="text-gray-600 hover:text-orange-500 transition-colors text-sm">
                  Group Tours
                </Link>
              </li>
              <li>
                <Link to="/opal-store" className="text-gray-600 hover:text-orange-500 transition-colors text-sm">
                  Opal Store
                </Link>
              </li>
              <li>
                <Link to="/online-store" className="text-gray-600 hover:text-orange-500 transition-colors text-sm">
                  Online Store
                </Link>
              </li>
              <li>
                <Link to="/webcam" className="text-gray-600 hover:text-orange-500 transition-colors text-sm">
                  Webcams
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-600 hover:text-orange-500 transition-colors text-sm">
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-4 heritage-heading">Visit Us</h4>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-600">
                    14 Hutchison St<br />
                    Coober Pedy SA 5723<br />
                    Australia
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-orange-500" />
                <p className="text-sm text-gray-600">(08) 8672 5288</p>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-orange-500" />
                <p className="text-sm text-gray-600">admin@umoonaopalmine.com.au</p>
              </div>
            </div>
          </div>

          {/* Opening Hours */}
          <div>
            <h4 className="text-lg font-semibold mb-4 heritage-heading">Opening Hours</h4>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <Clock className="w-5 h-5 text-orange-500 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-700 font-medium">General Hours</p>
                  <OpeningHours className="text-sm text-gray-600" />
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Clock className="w-5 h-5 text-orange-500 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-700 font-medium">Tours</p>
                  <p className="text-sm text-gray-600">10am, 2pm, 4pm daily</p>
                  {tourSchedule.restrict_monday_friday && (
                    <p className="text-xs text-gray-500">(Mon & Fri: 4pm only)</p>
                  )}
                  {openingHours.closed_on_tuesdays && (
                    <p className="text-xs text-red-600 font-semibold">Closed on Tuesdays</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Separator className="bg-gray-300" />

      {/* Bottom Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <p className="text-sm text-gray-600">
            © {currentYear} Umoona Opal Mine & Museum. Coober Pedy's iconic underground experience.
          </p>
          <div className="flex space-x-6 text-sm">
            <p className="text-gray-600">
              Website Design by{" "}
              <a 
                href="https://www.turbo.net.au" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-orange-500 hover:text-orange-600 font-medium transition-colors"
              >
                Turbo 360
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;